// ./InterviewScheduling.jsx
import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  ButtonGroup,
  Divider,
  Badge,
  Skeleton,
  SkeletonText,
  Stack,
  Heading,
  Text,
  Select,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchInterviewsAsync,
  selectInterview,
  selectInterviewLoading,
  selectInterviewPagination,
  selectInterviewError,
  updateInterviewAsync,
} from "./Candidate/CandidateSlice/Interview/InterviewSlice";
import { useForm } from "react-hook-form";

import noData from "./nodata1.png";

const STATUS_LABELS = {
  awaiting_confirmation: "Awaiting confirmation",
  scheduled: "Scheduled",
  rejected: "Rejected",
  selected: "Selected",
  hold: "On hold",
};

const STATUS_COLOR_SCHEMES = {
  awaiting_confirmation: "orange",
  scheduled: "blue",
  rejected: "red",
  selected: "green",
  hold: "purple",
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const label =
    STATUS_LABELS[status] || status.replace(/_/g, " ");
  const colorScheme = STATUS_COLOR_SCHEMES[status] || "gray";

  return (
    <Badge colorScheme={colorScheme} textTransform="capitalize">
      {label}
    </Badge>
  );
};

const InterviewSkeleton = () => (
  <Stack spacing={4}>
    {[1, 2, 3].map((item) => (
      <Box
        key={item}
        p={4}
        borderWidth="1px"
        borderRadius="md"
        bg="white"
      >
        <Flex justify="space-between" mb={2}>
          <SkeletonText
            noOfLines={1}
            width="60%"
            skeletonHeight="4"
          />
          <Skeleton height="36px" width="120px" />
        </Flex>
        <Divider my={2} />
        <SkeletonText
          noOfLines={1}
          width="40%"
          mb={2}
        />
        <SkeletonText
          noOfLines={1}
          width="30%"
          mb={2}
        />
        <SkeletonText
          noOfLines={1}
          width="50%"
          mb={2}
        />
        <Skeleton height="20px" width="200px" />
      </Box>
    ))}
  </Stack>
);

const InterviewCards = ({
  interviews,
  loading,
  pagination,
  onPageChange,
  onReschedule,
}) => {
  if (loading) {
    return <InterviewSkeleton />;
  }

  if (!interviews || interviews.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center w-full py-8">
        <img
          src={noData || "/placeholder.svg"}
          className="object-contain h-[260px] w-[260px] mb-4"
          alt="No interviews"
        />
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          No Interviews Available
        </h3>
        <p className="text-gray-600 text-center max-w-md text-sm">
          We couldn't find any interviews for the selected filters.
          Try changing the type or status filters.
        </p>
      </div>
    );
  }

  const handlePrev = () => {
    if (!pagination) return;
    if (pagination.currentPage <= 1) return;
    onPageChange(pagination.currentPage - 1);
  };

  const handleNext = () => {
    if (!pagination) return;
    if (pagination.currentPage >= pagination.lastPage) return;
    onPageChange(pagination.currentPage + 1);
  };

  return (
    <div className="flex flex-col gap-3">
      {interviews.map((interview) => (
        <Box
          key={interview.uuid || interview.id}
          borderWidth="1px"
          borderRadius="md"
          bg="white"
          p={4}
        >
          <Flex justify="space-between" flexWrap="wrap" mb={2}>
            <Box mr={4}>
              <Text
                fontSize="md"
                fontWeight="semibold"
                textTransform="capitalize"
              >
                {interview.call_type} interview with{" "}
                {interview.candidate_name}
              </Text>
              <Text
                fontSize="sm"
                color="gray.600"
              >
                {interview.job_title}
                {interview.job_department
                  ? ` · ${interview.job_department}`
                  : ""}
              </Text>
              {interview.hiring_company_name && (
                <Text
                  fontSize="sm"
                  color="gray.600"
                >
                  {interview.hiring_company_name}
                </Text>
              )}
            </Box>

            <Flex
              direction="column"
              align="flex-end"
              gap={2}
              mt={{ base: 2, md: 0 }}
            >
              <StatusBadge status={interview.status} />
              <ButtonGroup
                variant="outline"
                size="sm"
                spacing="3"
              >
                <Button
                  colorScheme="blue"
                  onClick={() => onReschedule(interview)}
                >
                  Reschedule
                </Button>
              </ButtonGroup>
            </Flex>
          </Flex>

          <Divider my={2} />

          <Flex
            justify="space-between"
            flexWrap="wrap"
            rowGap={2}
            columnGap={4}
          >
            <Box>
              <Text
                fontSize="xs"
                color="gray.500"
                mb={1}
              >
                Date & Time
              </Text>
              <Text fontSize="sm">
                {interview.interview_date} ·{" "}
                <span className="font-normal">
                  {interview.interview_time?.slice(0, 5)}
                </span>
              </Text>
            </Box>

            <Box>
              <Text
                fontSize="xs"
                color="gray.500"
                mb={1}
              >
                Duration
              </Text>
              <Text fontSize="sm">
                {interview.duration} minutes
              </Text>
            </Box>

            <Box>
              <Text
                fontSize="xs"
                color="gray.500"
                mb={1}
              >
                Candidate Contact
              </Text>
              <Text fontSize="sm">
                {interview.candidate_email}
                {interview.candidate_phone
                  ? ` · ${interview.candidate_phone}`
                  : ""}
              </Text>
            </Box>
          </Flex>
        </Box>
      ))}

      {pagination && pagination.total > pagination.perPage && (
        <Flex
          justify="space-between"
          align="center"
          mt={4}
          flexWrap="wrap"
          rowGap={2}
        >
          <Text fontSize="sm" color="gray.600">
            Showing {pagination.from}–{pagination.to} of{" "}
            {pagination.total}
          </Text>
          <ButtonGroup size="sm" variant="outline">
            <Button
              onClick={handlePrev}
              isDisabled={!pagination || pagination.currentPage <= 1}
            >
              Previous
            </Button>
            <Button
              onClick={handleNext}
              isDisabled={
                !pagination ||
                pagination.currentPage >= pagination.lastPage
              }
            >
              Next
            </Button>
          </ButtonGroup>
        </Flex>
      )}
    </div>
  );
};

const InterviewScheduling = () => {
  const dispatch = useDispatch();

  const interviews = useSelector(selectInterview);
  const loading = useSelector(selectInterviewLoading);
  const pagination = useSelector(selectInterviewPagination);
  const error = useSelector(selectInterviewError);

  const [activeTab, setActiveTab] = useState(0); // 0: upcoming, 1: today, 2: past
  const tabTypes = ["upcoming", "today", "past"];

  const [statusFilter, setStatusFilter] = useState("");
  const [perPageCount, setPerPageCount] = useState(15);
  const [page, setPage] = useState(1);

  const {
    isOpen: isEditModalOpen,
    onOpen: openEditModal,
    onClose: closeEditModal,
  } = useDisclosure();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const [selectedInterview, setSelectedInterview] =
    useState(null);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [activeTab, statusFilter, perPageCount]);

  // Fetch interviews when filters / page change
  useEffect(() => {
    const type = tabTypes[activeTab];
    dispatch(
      fetchInterviewsAsync({
        type,
        status: statusFilter || undefined,
        perPageCount,
        page,
      })
    );
  }, [dispatch, activeTab, statusFilter, perPageCount, page]);

  const onSubmit = async (data) => {
    if (!selectedInterview) return;

    try {
      await dispatch(
        updateInterviewAsync({
          uuid: selectedInterview.uuid,
          data,
        })
      ).unwrap();

      const type = tabTypes[activeTab];
      await dispatch(
        fetchInterviewsAsync({
          type,
          status: statusFilter || undefined,
          perPageCount,
          page,
        })
      );

      setSelectedInterview(null);
      reset();
      closeEditModal();
    } catch (error) {
      console.error("Failed to update interview", error);
    }
  };

  const openUpdateModal = (interview) => {
    setSelectedInterview(interview);

    setValue("duration", interview.duration || 30);
    setValue("call_type", interview.call_type || "video");
    setValue("message", interview.message || "");
    setValue("status", interview.status || "scheduled");
    setValue(
      "hiring_team_email",
      interview.hiring_team_email || ""
    );
    setValue("interview_date", interview.interview_date || "");
    setValue(
      "interview_time",
      interview.interview_time
        ? interview.interview_time.slice(0, 5)
        : ""
    );

    openEditModal();
  };

  const handlePageChange = (newPage) => {
    if (!pagination) return;
    if (
      newPage < 1 ||
      newPage > pagination.lastPage ||
      newPage === page
    ) {
      return;
    }
    setPage(newPage);
  };

  const handleCloseModal = () => {
    reset();
    setSelectedInterview(null);
    closeEditModal();
  };

  return (
    <div className="bg-mainBg p-3">
      <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
        <Box mb={4}>
          <Heading
            as="h1"
            size="lg"
            mb={2}
            textAlign="left"
          >
            Scheduled Interviews
          </Heading>
          <Text
            fontSize="sm"
            color="gray.600"
          >
            Manage and reschedule your interviews with
            candidates.
          </Text>
        </Box>

        {error && (
          <Box
            mb={4}
            p={3}
            borderWidth="1px"
            borderRadius="md"
            bg="red.50"
            borderColor="red.200"
          >
            <Text
              fontSize="sm"
              color="red.700"
            >
              {error}
            </Text>
          </Box>
        )}

        {/* Filters bar */}
        <Box mb={4}>
          <Flex
            justify="space-between"
            align="center"
            flexWrap="wrap"
            gap={3}
          >
            <Flex align="center" gap={2}>
              <Text
                fontSize="sm"
                color="gray.600"
              >
                Filter by status:
              </Text>
              <Select
                size="sm"
                width="220px"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value)
                }
              >
                <option value="">All statuses</option>
                <option value="awaiting_confirmation">
                  Awaiting confirmation
                </option>
                <option value="scheduled">Scheduled</option>
                <option value="selected">Selected</option>
                <option value="hold">On hold</option>
                <option value="rejected">Rejected</option>
              </Select>
            </Flex>

            <Flex align="center" gap={2}>
              <Text
                fontSize="sm"
                color="gray.600"
              >
                Items per page:
              </Text>
              <Select
                size="sm"
                width="100px"
                value={perPageCount}
                onChange={(e) =>
                  setPerPageCount(Number(e.target.value))
                }
              >
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Select>
            </Flex>
          </Flex>
        </Box>

        <div className="w-full bg-white rounded-xl">
          <Tabs
            variant="enclosed"
            index={activeTab}
            onChange={(index) => setActiveTab(index)}
            isFitted
            colorScheme="blue"
          >
            <TabList>
              <Tab
                fontWeight="medium"
                fontSize="sm"
                _selected={{
                  color: "blue.600",
                  borderColor: "blue.600",
                }}
              >
                Upcoming Interviews
              </Tab>
              <Tab
                fontWeight="medium"
                fontSize="sm"
                _selected={{
                  color: "blue.600",
                  borderColor: "blue.600",
                }}
              >
                Today
              </Tab>
              <Tab
                fontWeight="medium"
                fontSize="sm"
                _selected={{
                  color: "blue.600",
                  borderColor: "blue.600",
                }}
              >
                Past
              </Tab>
            </TabList>

            <TabPanels>
              {[0, 1, 2].map((tabIndex) => (
                <TabPanel key={tabIndex}>
                  <InterviewCards
                    interviews={interviews}
                    loading={loading}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onReschedule={openUpdateModal}
                  />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>

          {/* Edit / Reschedule Modal */}
          <Modal
            isOpen={isEditModalOpen}
            onClose={handleCloseModal}
            size="5xl"
            className="mt-0"
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader className="text-lg font-bold">
                Edit Interview Details
                {selectedInterview?.candidate_name
                  ? ` – ${selectedInterview.candidate_name}`
                  : ""}
              </ModalHeader>
              <ModalCloseButton />
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-wrap border-b">
                  <div className="w-full md:w-1/2 px-3 pr-4 pt-6 bg-gray-100">
                    <div className="mb-4">
                      <label
                        className="block text-blue-700 font-bold mb-2"
                        htmlFor="duration"
                      >
                        Duration
                      </label>
                      <select
                        {...register("duration", {
                          required: "Duration is required",
                        })}
                        className="block w-full border rounded-md p-2"
                      >
                        <option value="30">
                          30 minutes
                        </option>
                        <option value="60">1 hour</option>
                        <option value="90">
                          1.5 hours
                        </option>
                        <option value="120">
                          2 hours
                        </option>
                      </select>
                      {errors.duration && (
                        <span className="text-red-500 text-sm">
                          {errors.duration.message}
                        </span>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="block text-blue-700 font-bold mb-2">
                        Call Type
                      </label>
                      <div className="flex flex-wrap gap-4">
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            {...register("call_type", {
                              required:
                                "Call type is required",
                            })}
                            value="video"
                            className="form-radio text-blue-600"
                          />
                          <span className="ml-2">
                            Video Call
                          </span>
                        </label>

                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            {...register("call_type")}
                            value="voice"
                            className="form-radio text-blue-600"
                          />
                          <span className="ml-2">
                            Voice Call
                          </span>
                        </label>

                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            {...register("call_type")}
                            value="walk-in"
                            className="form-radio text-blue-600"
                          />
                          <span className="ml-2">
                            Walk-In
                          </span>
                        </label>
                      </div>
                      {errors.call_type && (
                        <span className="text-red-500 text-sm">
                          {errors.call_type.message}
                        </span>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 font-bold mb-2">
                        Message to Candidate
                      </label>
                      <textarea
                        {...register("message", {
                          required:
                            "Message is required",
                        })}
                        rows={3}
                        className="block w-full border rounded-md p-2"
                        placeholder="Enter your message..."
                      />
                      {errors.message && (
                        <span className="text-red-500 text-sm">
                          {errors.message.message}
                        </span>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 font-bold mb-2">
                        Hiring Team Email
                      </label>
                      <input
                        type="email"
                        {...register(
                          "hiring_team_email",
                          {
                            required:
                              "Email is required",
                          }
                        )}
                        className="block w-full border rounded-md p-2"
                        placeholder="example@company.com"
                      />
                      {errors.hiring_team_email && (
                        <span className="text-red-500 text-sm">
                          {
                            errors
                              .hiring_team_email
                              .message
                          }
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="w-full md:w-1/2 px-6 pt-6 pl-4">
                    <div className="mb-4">
                      <label className="block text-blue-700 font-bold mb-2">
                        Interview Date
                      </label>
                      <input
                        type="date"
                        {...register(
                          "interview_date",
                          {
                            required:
                              "Interview date is required",
                          }
                        )}
                        className="block w-full border rounded-md p-2"
                      />
                      {errors.interview_date && (
                        <span className="text-red-500 text-sm">
                          {
                            errors.interview_date
                              .message
                          }
                        </span>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="block text-blue-700 font-bold mb-2">
                        Starting Time
                      </label>
                      <input
                        type="time"
                        {...register(
                          "interview_time",
                          {
                            required:
                              "Start time is required",
                          }
                        )}
                        className="block w-full border rounded-md p-2"
                      />
                      {errors.interview_time && (
                        <span className="text-red-500 text-sm">
                          {
                            errors.interview_time
                              .message
                          }
                        </span>
                      )}
                    </div>
                    <div className="mb-4">
                      <label
                        className="block text-blue-700 font-bold mb-2"
                        htmlFor="Status"
                      >
                        Status
                      </label>
                      <select
                        {...register("status", {
                          required: "status is required",
                        })}
                        className="block w-full border rounded-md p-2"
                      >
                        <option value="scheduled">scheduled</option>

                        <option value="selected">
                          selected
                        </option>
                        <option value="rejected">
                          rejected
                        </option>

                        <option value="hold">
                          hold
                        </option>
                      </select>
                      {errors.Status && (
                        <span className="text-red-500 text-sm">
                          {errors.Status.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <ModalFooter>
                  <Button
                    className="mr-3 px-3 border rounded-lg bg-white text-gray-500"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </Button>
                  <button
                    className="bg-blue-700 text-white px-4 py-2 rounded-lg"
                    type="submit"
                  >
                    Save Changes
                  </button>
                </ModalFooter>
              </form>
            </ModalContent>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default InterviewScheduling;